import React from 'react';
import cn from 'classnames';
import { IBaseElementProps } from '../BasicElement';
import Carousel from '../Carousel';
import CarouselItem from '../CarouselItem';
import {kebabCase} from 'lodash';
import VHSBlogRollTile from "../VHSBlogRoll/VHSBlogRollTile";

export interface IVHSPostProps {
	blogroll?: string[];
	termMap?: string[];
}

/**
 * @useTermData
 * @taxonomy category
 */
export interface ICategoryData {
	term_id?: number;
	name?: string;
	url?: string;
}

/**
 * @useTermData
 * @taxonomy post_tag
 */
export interface ITagData {
	term_id?: number;
	name?: string;
	url?: string;
}

/**
 * @useTermData
 * @taxonomy department
 */
export interface IDepartmentData {
	term_id?: number;
	name?: string;
	url?: string;
}

export interface IVHSRecentPostsProps extends IBaseElementProps {
	/**
	 @noUI
	 * Please provide a unique ID. It should be lowercase with hyphens separating the words, like main-testimonial. Do not include spaces or any other characters. Try to keep it relatively short.
	 */
	id?: string;
	/**
	 * Style
	 */
	style: 'default' | 'wide';
	/**
	 * Heading
	 */
	heading?: string;
	/**
	 * Leave blank to allow automatic detection
	 */
	categories?: ICategoryData[];
	/**
	 * Leave blank to allow automatic detection
	 */
	tags?: ITagData[];
	/**
	 * Leave blank to allow automatic detection
	 */
	departments?: IDepartmentData[];
	/**
	 * @noUI
	 */
	posts?: IVHSPostProps[];
	/**
	 * @noUI
	 */
	termMap?: string[];
}

/**
 * Recent Posts
 * @block
 * @icon sticky
 * @requiresHooks
 */
const VHSRecentPosts: React.FC<IVHSRecentPostsProps> = ({
	id = '1',
	style,
	heading,
	posts = [],
	termMap= [],
}) => {
	const peek = (style!='wide') ? 284 : 406;

	const carouselOptions = {
		type: 'carousel',
		rewind: false,
		bound: true,
		gap: 32,
		perView: 1,
		peek: {
			before: 0,
			after: peek,
		},
		breakpoints: {
			576: {
				perView: 1,
				peek: {
					before: 0,
					after: 0,
				},
			},
			768: {
				perView: 2,
				peek: {
					before: 0,
					after: 0,
				},
			},
			992: {
				perView: 3,
				peek: {
					before: 0,
					after: 0,
				},
			},
			1200: {
				perView: 3,
				peek: {
					before: 0,
					after: 0,
				},
			},
			5000: {
				perView: style === 'wide' ? 4 : 3,
				peek: {
					before: 0,
					after: 0,
				},
			},
		},
	};

	return (
		<>
			{posts.length > 0 &&
			<div
				className={cn(
					'vhs-recent-posts',
					(style === 'wide') && 'vhs-recent-posts--wide',
				)}
			>
				<div className="vhs-recent-posts__container">
					{heading && <h2 className="vhs-recent-posts__heading">{heading}</h2>}
					{posts?.length > 0 &&
					<div className="vhs-recent-posts__wrapper">
						<Carousel
							perView={1}
							options={carouselOptions}
							id={`recent-posts-${id || kebabCase(heading)}`}
						>
							{posts.map((post, index) => (
								<CarouselItem key={index}>
									<VHSBlogRollTile
										{...post}
										termMap={termMap}
									/>
								</CarouselItem>
							))}
						</Carousel>
					</div>
					}
				</div>
			</div>
			}
		</>
	);
};

export default VHSRecentPosts;
